import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  CogIcon, 
  ShieldCheckIcon,
  BellIcon,
  UserIcon,
  DocumentTextIcon,
  ServerIcon,
  ScaleIcon,
  BeakerIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Produit } from '@/types';
import weighingService from '@/services/weighing';
import settingsService from '@/services/settingsService';
import SearchableSelect from '@/components/SearchableSelect';
import MotifRefoulementSelector from '@/components/MotifRefoulementSelector';
import MotifRefoulementDetails from '@/components/MotifRefoulementDetails';

interface QualityStandard {
  id: string;
  product: string;
  maxMoisture: number;
  minProteins: number;
  minHectoliterWeight: number;
  maxImpurities: number;
  maxBrokenGrains: number;
  acceptedQualities: string[];
}

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const navigate = useNavigate();
  
  // État pour les produits
  const [products, setProducts] = useState<Produit[]>([]);
  
  // État pour la gestion des paramètres
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  
  // Types pour les paramètres
  interface GeneralSettings {
    company_name: string;
    website: string;
    address: string;
    phone: string;
    language: string;
    timezone: string;
    currency: string;
  }

  interface SecuritySettings {
    two_factor_auth: boolean;
    multiple_sessions: boolean;
    password_min_length: number;
    password_expiration_days: number;
  }

  interface NotificationSettings {
    daily_reports: boolean;
    stock_alerts: boolean;
    quality_alerts: boolean;
  }

  interface SystemSettings {
    database_type: string;
    database_version: string;
    maintenance_mode: boolean;
  }

  // États pour les paramètres de chaque onglet
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({
    company_name: 'Coopérative Céréalière Africaine (CCA)',
    website: 'https://www.cca.ci',
    address: '123 Avenue des Céréales, Abidjan',
    phone: '+225 01234567',
    language: 'fr',
    timezone: 'Africa/Abidjan',
    currency: 'XOF'
  });
  
  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    two_factor_auth: true,
    multiple_sessions: false,
    password_min_length: 8,
    password_expiration_days: 90
  });
  
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    daily_reports: true,
    stock_alerts: true,
    quality_alerts: true
  });
  
  const [systemSettings, setSystemSettings] = useState<SystemSettings>({
    database_type: 'PostgreSQL',
    database_version: '14.0',
    maintenance_mode: false
  });

  // État pour les motifs de refoulement
  const [selectedMotif, setSelectedMotif] = useState<string>('');
  const [motifs, setMotifs] = useState<any[]>([]);
  const [motifsLoading, setMotifsLoading] = useState(true);
  const [motifsError, setMotifsError] = useState<string>('');
  
  // Fonction pour charger les produits
  const fetchProducts = async () => {
    try {
      const response = await weighingService.getProduits();
      if (response && response.results) {
        setProducts(response.results);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
    }
  };

  // Fonction pour charger les motifs de refoulement
  const loadMotifs = async () => {
    try {
      setMotifsLoading(true);
      setMotifsError('');
      const motifsData = await settingsService.getMotifsRefoulement();
      setMotifs(motifsData);
    } catch (err) {
      console.error('Erreur lors du chargement des motifs:', err);
      setMotifsError('Erreur lors du chargement des motifs de refoulement');
    } finally {
      setMotifsLoading(false);
    }
  };

  // Fonction pour charger les paramètres d'un onglet
  const loadTabSettings = async (tab: string) => {
    try {
      const response = await settingsService.getSetting(tab);
      
      if (response.success) {
        // Si response.data est null (paramètre n'existe pas), utiliser des valeurs par défaut
        const settings = response.data?.json_value || {};
        
        // Mettre à jour l'état correspondant selon l'onglet
        switch (tab) {
          case 'general':
            setGeneralSettings(settings || {});
            break;
          case 'security':
            setSecuritySettings(settings || {});
            break;
          case 'notifications':
            setNotificationSettings(settings || {});
            break;
          case 'system':
            setSystemSettings(settings || {});
            break;
          case 'quality-standards':
            if (settings && Array.isArray(settings)) {
              setQualityStandards(settings);
            }
            break;
        }
      }
    } catch (error) {
      console.error(`Erreur lors du chargement des paramètres ${tab}:`, error);
    }
  };



  // Fonction pour sauvegarder tous les paramètres
  const saveAllSettings = async () => {
    try {
      setIsSaving(true);
      setSaveMessage(null);
      
      // Collecter tous les paramètres avec une setting_name par onglet
      const allSettings: Record<string, any> = {};
      
      // Général - setting_name: 'general'
      allSettings['general'] = generalSettings;
      
      // Sécurité - setting_name: 'security'
      allSettings['security'] = securitySettings;
      
      // Notifications - setting_name: 'notifications'
      allSettings['notifications'] = notificationSettings;
      
      // Système - setting_name: 'system'
      allSettings['system'] = systemSettings;
      
      // Standards qualité - setting_name: 'quality-standards'
      allSettings['quality-standards'] = qualityStandards;
      
      const response = await settingsService.bulkSaveSettings({
        settings: allSettings
      });
      
      if (response.success) {
        setSaveMessage({
          type: 'success',
          text: response.message || 'Tous les paramètres ont été sauvegardés avec succès'
        });
      } else {
        setSaveMessage({
          type: 'error',
          text: response.message || 'Erreur lors de la sauvegarde'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de tous les paramètres:', error);
      setSaveMessage({
        type: 'error',
        text: 'Erreur lors de la sauvegarde des paramètres'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Charger les produits au montage du composant
  useEffect(() => {
    fetchProducts();
  }, []);

  // Charger les paramètres de l'onglet actif
  useEffect(() => {
    loadTabSettings(activeTab);
    if (activeTab === 'motifs-refoulement') {
      loadMotifs();
    }
  }, [activeTab]);
  
  // État pour les standards qualité
  const [qualityStandards, setQualityStandards] = useState<QualityStandard[]>([
    {
      id: '1',
      product: 'Blé tendre',
      maxMoisture: 14.0,
      minProteins: 11.0,
      minHectoliterWeight: 76.0,
      maxImpurities: 1.0,
      maxBrokenGrains: 2.0,
      acceptedQualities: ['excellente', 'bonne', 'moyenne']
    },
    {
      id: '2',
      product: 'Maïs',
      maxMoisture: 15.0,
      minProteins: 8.0,
      minHectoliterWeight: 70.0,
      maxImpurities: 2.0,
      maxBrokenGrains: 3.0,
      acceptedQualities: ['excellente', 'bonne']
    }
  ]);

  // Fonctions pour gérer les standards qualité
  const addQualityStandard = (productName?: string) => {
    const newStandard: QualityStandard = {
      id: Date.now().toString(),
      product: productName || '',
      maxMoisture: 14.0,
      minProteins: 11.0,
      minHectoliterWeight: 76.0,
      maxImpurities: 1.0,
      maxBrokenGrains: 2.0,
      acceptedQualities: ['excellente', 'bonne']
    };
    setQualityStandards([...qualityStandards, newStandard]);
  };

  const updateQualityStandard = (id: string, field: keyof QualityStandard, value: any) => {
    setQualityStandards(standards =>
      standards.map(standard =>
        standard.id === id ? { ...standard, [field]: value } : standard
      )
    );
  };

  const removeQualityStandard = (id: string) => {
    setQualityStandards(standards => standards.filter(standard => standard.id !== id));
  };

  const toggleAcceptedQuality = (standardId: string, quality: string) => {
    setQualityStandards(standards =>
      standards.map(standard => {
        if (standard.id === standardId) {
          const updatedQualities = standard.acceptedQualities.includes(quality)
            ? standard.acceptedQualities.filter(q => q !== quality)
            : [...standard.acceptedQualities, quality];
          return { ...standard, acceptedQualities: updatedQualities };
        }
        return standard;
      })
    );
  };

  const tabs = [
    { id: 'general', label: 'Général', icon: CogIcon },
    { id: 'security', label: 'Sécurité', icon: ShieldCheckIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'quality-standards', label: 'Standards qualité', icon: BeakerIcon },
    { id: 'motifs-refoulement', label: 'Motifs de refoulement', icon: ExclamationTriangleIcon },
    { id: 'system', label: 'Système', icon: ServerIcon },
    { id: 'users', label: 'Utilisateurs', icon: UserIcon },
    { id: 'reports', label: 'Rapports', icon: DocumentTextIcon },
    { id: 'ponts-bascule', label: 'Ponts Bascule', icon: ScaleIcon }
  ];

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-medium text-dark-black mb-4">Informations de l'entreprise</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">Nom de l'entreprise</label>
            <input
              type="text"
              value={generalSettings.company_name || ''}
              onChange={(e) => setGeneralSettings(prev => ({...prev, company_name: e.target.value}))}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Site web</label>
            <input
              type="url"
              value={generalSettings.website || ''}
              onChange={(e) => setGeneralSettings(prev => ({...prev, website: e.target.value}))}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Adresse</label>
            <input
              type="text"
              value={generalSettings.address || ''}
              onChange={(e) => setGeneralSettings(prev => ({...prev, address: e.target.value}))}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Téléphone</label>
            <input
              type="tel"
              value={generalSettings.phone || ''}
              onChange={(e) => setGeneralSettings(prev => ({...prev, phone: e.target.value}))}
              className="form-input"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-medium text-dark-black mb-4">Paramètres régionaux</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="form-label">Langue</label>
            <select 
              value={generalSettings.language || 'fr'}
              onChange={(e) => setGeneralSettings(prev => ({...prev, language: e.target.value}))}
              className="form-select"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="form-label">Fuseau horaire</label>
            <select 
              value={generalSettings.timezone || 'Africa/Abidjan'}
              onChange={(e) => setGeneralSettings(prev => ({...prev, timezone: e.target.value}))}
              className="form-select"
            >
              <option value="Africa/Abidjan">Afrique/Abidjan (GMT+0)</option>
              <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
            </select>
          </div>
          <div>
            <label className="form-label">Devise</label>
            <select 
              value={generalSettings.currency || 'XOF'}
              onChange={(e) => setGeneralSettings(prev => ({...prev, currency: e.target.value}))}
              className="form-select"
            >
              <option value="XOF">Franc CFA (XOF)</option>
              <option value="EUR">Euro (EUR)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-medium text-dark-black mb-4">Authentification</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-light-gray rounded-lg">
            <div>
              <h4 className="font-medium text-dark-black">Authentification à deux facteurs</h4>
              <p className="text-sm text-gray-600">Sécurisez votre compte avec un code supplémentaire</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={securitySettings.two_factor_auth || false}
                onChange={(e) => setSecuritySettings(prev => ({...prev, two_factor_auth: e.target.checked}))}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-bright-orange rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-bright-orange"></div>
            </label>
          </div>
          <div className="flex items-center justify-between p-4 bg-light-gray rounded-lg">
            <div>
              <h4 className="font-medium text-dark-black">Sessions multiples</h4>
              <p className="text-sm text-gray-600">Permettre plusieurs connexions simultanées</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={securitySettings.multiple_sessions || false}
                onChange={(e) => setSecuritySettings(prev => ({...prev, multiple_sessions: e.target.checked}))}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-bright-orange rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-bright-orange"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-medium text-dark-black mb-4">Politique de mots de passe</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">Longueur minimale</label>
            <input
              type="number"
              value={securitySettings.password_min_length || 8}
              onChange={(e) => setSecuritySettings(prev => ({...prev, password_min_length: parseInt(e.target.value)}))}
              min="6"
              max="20"
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Expiration (jours)</label>
            <input
              type="number"
              value={securitySettings.password_expiration_days || 90}
              onChange={(e) => setSecuritySettings(prev => ({...prev, password_expiration_days: parseInt(e.target.value)}))}
              min="30"
              max="365"
              className="form-input"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-medium text-dark-black mb-4">Notifications par email</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-light-gray rounded-lg">
            <div>
              <h4 className="font-medium text-dark-black">Rapports quotidiens</h4>
              <p className="text-sm text-gray-600">Recevoir un résumé quotidien des activités</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={notificationSettings.daily_reports || false}
                onChange={(e) => setNotificationSettings(prev => ({...prev, daily_reports: e.target.checked}))}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-bright-orange rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-bright-orange"></div>
            </label>
          </div>
          <div className="flex items-center justify-between p-4 bg-light-gray rounded-lg">
            <div>
              <h4 className="font-medium text-dark-black">Alertes de stock</h4>
              <p className="text-sm text-gray-600">Notifications quand le stock est faible</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={notificationSettings.stock_alerts || false}
                onChange={(e) => setNotificationSettings(prev => ({...prev, stock_alerts: e.target.checked}))}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-bright-orange rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-bright-orange"></div>
            </label>
          </div>
          <div className="flex items-center justify-between p-4 bg-light-gray rounded-lg">
            <div>
              <h4 className="font-medium text-dark-black">Alertes de qualité</h4>
              <p className="text-sm text-gray-600">Notifications pour les refoulements qualité</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={notificationSettings.quality_alerts || false}
                onChange={(e) => setNotificationSettings(prev => ({...prev, quality_alerts: e.target.checked}))}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-bright-orange rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-bright-orange"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderQualityStandardsSettings = () => (
    <div className="space-y-6">
      {/* Section de sélection du produit pour nouveaux critères */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-medium text-dark-black mb-2">Standards qualité par produit</h3>
            <p className="text-sm text-gray-600">
              Configurez les critères d'acceptation pour l'analyse qualité de chaque produit
            </p>
          </div>
        </div>

        {/* Sélection du produit pour ajouter de nouveaux critères */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-3 flex items-center">
            <BeakerIcon className="h-5 w-5 mr-2" />
            Ajouter des critères pour un produit
          </h4>
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="form-label">Sélectionner un produit</label>
              <SearchableSelect
                options={products
                  .filter(product => !qualityStandards.find(s => s.product === product.nom))
                  .map(product => ({
                    value: product.nom,
                    label: `${product.nom} (${product.type_display})`
                  }))}
                value=""
                onChange={(value) => {
                  if (value && !qualityStandards.find(s => s.product === value)) {
                    addQualityStandard(value);
                  }
                }}
                placeholder="Choisir un produit pour configurer ses critères..."
                className="w-full"
              />
            </div>
            <button
              onClick={() => {
                const availableProducts = products.filter(p => 
                  !qualityStandards.find(s => s.product === p.nom)
                );
                if (availableProducts.length > 0) {
                  addQualityStandard(availableProducts[0].nom);
                }
              }}
              className="group flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-300 transform hover:scale-105"
            >
              <PlusIcon className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">Ajouter</span>
            </button>
          </div>
          <p className="text-xs text-blue-700 mt-2">
            💡 Seuls les produits non encore configurés apparaissent dans la liste
          </p>
        </div>

        {/* Liste des standards configurés */}
        <div className="space-y-6">
          {qualityStandards.map((standard) => (
            <div key={standard.id} className="border border-gray-200 rounded-xl p-6 bg-gradient-to-br from-white to-gray-50">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <BeakerIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-dark-black text-lg">
                      {standard.product || 'Nouveau produit'}
                    </h4>
                    <p className="text-sm text-gray-600">Critères d'acceptation qualité</p>
                  </div>
                </div>
                <button
                  onClick={() => removeQualityStandard(standard.id)}
                  className="group flex items-center justify-center w-10 h-10 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-300 transform hover:scale-110"
                >
                  <TrashIcon className="h-5 w-5 group-hover:scale-110 transition-transform" />
                </button>
              </div>

              {/* Critères numériques */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {/* Humidité max */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <label className="form-label text-sm font-medium text-gray-700">Humidité max (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={standard.maxMoisture}
                    onChange={(e) => updateQualityStandard(standard.id, 'maxMoisture', parseFloat(e.target.value))}
                    className="form-input mt-1"
                    placeholder="14.0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Seuil maximum d'humidité</p>
                </div>

                {/* Protéines min */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <label className="form-label text-sm font-medium text-gray-700">Protéines min (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={standard.minProteins}
                    onChange={(e) => updateQualityStandard(standard.id, 'minProteins', parseFloat(e.target.value))}
                    className="form-input mt-1"
                    placeholder="11.0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Teneur minimale en protéines</p>
                </div>

                {/* Poids HL min */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <label className="form-label text-sm font-medium text-gray-700">Poids HL min (kg/hl)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={standard.minHectoliterWeight}
                    onChange={(e) => updateQualityStandard(standard.id, 'minHectoliterWeight', parseFloat(e.target.value))}
                    className="form-input mt-1"
                    placeholder="76.0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Poids hectolitrique minimum</p>
                </div>

                {/* Impuretés max */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <label className="form-label text-sm font-medium text-gray-700">Impuretés max (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={standard.maxImpurities}
                    onChange={(e) => updateQualityStandard(standard.id, 'maxImpurities', parseFloat(e.target.value))}
                    className="form-input mt-1"
                    placeholder="1.0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Pourcentage maximum d'impuretés</p>
                </div>

                {/* Grains cassés max */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <label className="form-label text-sm font-medium text-gray-700">Grains cassés max (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={standard.maxBrokenGrains}
                    onChange={(e) => updateQualityStandard(standard.id, 'maxBrokenGrains', parseFloat(e.target.value))}
                    className="form-input mt-1"
                    placeholder="2.0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Pourcentage maximum de grains cassés</p>
                </div>

                {/* Chiffres de qualité (pour noix d'anacarde) */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <label className="form-label text-sm font-medium text-gray-700">Chiffres qualité</label>
                  <input
                    type="text"
                    placeholder="Ex: 180/200, 200/220"
                    className="form-input mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Spécifique aux noix d'anacarde</p>
                </div>
              </div>

              {/* Qualités acceptées */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <label className="form-label text-sm font-medium text-gray-700 mb-3">Qualités acceptées</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {['excellente', 'bonne', 'moyenne', 'limite', 'mauvaise'].map((quality) => (
                    <label key={quality} className="flex items-center space-x-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={standard.acceptedQualities.includes(quality)}
                        onChange={() => toggleAcceptedQuality(standard.id, quality)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm capitalize font-medium">{quality}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ✅ Seules les qualités cochées seront acceptées pour ce produit
                </p>
              </div>
            </div>
          ))}
        </div>

        {qualityStandards.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <BeakerIcon className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Aucun standard qualité configuré</h3>
            <p className="text-sm">Sélectionnez un produit ci-dessus pour commencer la configuration</p>
          </div>
        )}
      </div>

      {/* Informations sur les standards */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
          <CheckIcon className="h-5 w-5 mr-2" />
          Informations sur les standards qualité
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ul className="text-sm text-blue-800 space-y-2">
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Les critères définis ici seront utilisés pour l'évaluation automatique de la qualité</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Les produits qui dépassent les seuils seront marqués comme non conformes</span>
            </li>
          </ul>
          <ul className="text-sm text-blue-800 space-y-2">
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Seules les qualités cochées seront acceptées pour chaque produit</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Les standards peuvent être modifiés à tout moment</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderMotifsRefoulementSettings = () => {

    const getCriticiteColor = (criticite: string) => {
      switch (criticite) {
        case 'Critique':
          return 'text-red-600 bg-red-100 border-red-200';
        case 'Haute':
          return 'text-orange-600 bg-orange-100 border-orange-200';
        case 'Moyenne':
          return 'text-yellow-600 bg-yellow-100 border-yellow-200';
        case 'Faible':
          return 'text-green-600 bg-green-100 border-green-200';
        default:
          return 'text-gray-600 bg-gray-100 border-gray-200';
      }
    };

    const getCriticiteIcon = (criticite: string) => {
      switch (criticite) {
        case 'Critique':
          return 'fas fa-exclamation-triangle';
        case 'Haute':
          return 'fas fa-exclamation-circle';
        case 'Moyenne':
          return 'fas fa-info-circle';
        case 'Faible':
          return 'fas fa-check-circle';
        default:
          return 'fas fa-info';
      }
    };

    return (
      <div className="space-y-6">
        {/* Header avec statistiques */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-medium text-dark-black mb-2">Gestion des motifs de refoulement</h3>
              <p className="text-sm text-gray-600">
                Configurez et gérez les motifs de refoulement utilisés dans le système
              </p>
            </div>
            <button
              onClick={loadMotifs}
              className="group flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-300 transform hover:scale-105"
            >
              <i className="fas fa-sync-alt mr-2 group-hover:rotate-180 transition-transform"></i>
              <span className="text-sm font-medium">Actualiser</span>
            </button>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="fas fa-list text-white"></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-800">{motifs.length}</p>
                  <p className="text-sm text-blue-600">Motifs total</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="fas fa-exclamation-triangle text-white"></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-800">
                    {motifs.filter(m => m.criticite === 'Critique').length}
                  </p>
                  <p className="text-sm text-red-600">Critiques</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="fas fa-exclamation-circle text-white"></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-orange-800">
                    {motifs.filter(m => m.criticite === 'Haute').length}
                  </p>
                  <p className="text-sm text-orange-600">Haute priorité</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="fas fa-check-circle text-white"></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-800">
                    {motifs.filter(m => m.criticite === 'Faible').length}
                  </p>
                  <p className="text-sm text-green-600">Faible priorité</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sélecteur et détails */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sélecteur */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h4 className="font-medium text-dark-black mb-4">Sélectionner un motif</h4>
            <MotifRefoulementSelector
              selectedMotif={selectedMotif}
              onMotifChange={setSelectedMotif}
              showDescription={true}
              groupByCategory={true}
              className="w-full"
            />
          </div>

          {/* Détails */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h4 className="font-medium text-dark-black mb-4">Détails du motif</h4>
            {selectedMotif ? (
              <MotifRefoulementDetails
                motifId={selectedMotif}
                showHeader={false}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                <i className="fas fa-info-circle text-3xl mb-4"></i>
                <p>Sélectionnez un motif pour voir ses détails</p>
              </div>
            )}
          </div>
        </div>

        {/* Liste des motifs */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="font-medium text-dark-black">Tous les motifs de refoulement</h4>
          </div>
          
          {motifsLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des motifs...</p>
            </div>
          ) : motifsError ? (
            <div className="p-8 text-center">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <i className="fas fa-exclamation-triangle text-red-500 text-2xl mb-2"></i>
                <p className="text-red-600">{motifsError}</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {motifs.map((motif) => (
                <div
                  key={motif.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedMotif === motif.id ? 'bg-orange-50 border-r-4 border-orange-500' : ''
                  }`}
                  onClick={() => setSelectedMotif(motif.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h5 className="text-lg font-medium text-gray-900">{motif.libelle}</h5>
                        <span className="text-sm text-gray-500 font-mono">{motif.code}</span>
                      </div>
                      <p className="text-gray-600 mb-2">{motif.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <i className="fas fa-tag mr-1"></i>
                          {motif.categorie}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCriticiteColor(motif.criticite)}`}>
                        <i className={`${getCriticiteIcon(motif.criticite)} mr-1`}></i>
                        {motif.criticite}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Informations */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
            <i className="fas fa-info-circle mr-2"></i>
            Informations sur les motifs de refoulement
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ul className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Les motifs sont organisés par catégorie et niveau de criticité</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Chaque motif a un code unique pour faciliter l'identification</span>
              </li>
            </ul>
            <ul className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Les motifs critiques nécessitent une action immédiate</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Les motifs peuvent être utilisés lors des refoulements de pesées</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderSystemSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-medium text-dark-black mb-4">Base de données</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">Type de base</label>
            <input
              type="text"
              value={systemSettings.database_type || 'PostgreSQL'}
              onChange={(e) => setSystemSettings(prev => ({...prev, database_type: e.target.value}))}
              className="form-input bg-gray-100"
              disabled
            />
          </div>
          <div>
            <label className="form-label">Version</label>
            <input
              type="text"
              value={systemSettings.database_version || '14.0'}
              onChange={(e) => setSystemSettings(prev => ({...prev, database_version: e.target.value}))}
              className="form-input bg-gray-100"
              disabled
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-medium text-dark-black mb-4">Maintenance</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-light-gray rounded-lg">
            <div>
              <h4 className="font-medium text-dark-black">Mode maintenance</h4>
              <p className="text-sm text-gray-600">Activer le mode maintenance pour les mises à jour</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={systemSettings.maintenance_mode || false}
                onChange={(e) => setSystemSettings(prev => ({...prev, maintenance_mode: e.target.checked}))}
                className="sr-only peer" 
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-bright-orange rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-bright-orange"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPontsBasculeSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-medium text-dark-black mb-4">Configuration des Ponts Bascule</h3>
        <p className="text-gray-600 mb-6">
          Gérez la configuration des ponts bascule et de leurs Scale Agents pour chaque site.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="group p-6 border-2 border-dashed border-gray-300 rounded-xl text-center hover:border-orange-400 hover:bg-gradient-to-br hover:from-orange-50 hover:to-orange-100 transition-all duration-200 cursor-pointer transform hover:scale-105 shadow-sm hover:shadow-lg"
               onClick={() => navigate('/config/ponts-bascule')}>
            <ScaleIcon className="h-12 w-12 text-gray-400 group-hover:text-orange-500 mx-auto mb-4 transition-colors" />
            <h4 className="font-medium text-dark-black mb-2 group-hover:text-orange-700 transition-colors">Gérer les Ponts Bascule</h4>
            <p className="text-sm text-gray-600 group-hover:text-orange-600 transition-colors">
              Configurer les adresses IP, ports, et paramètres de communication
            </p>
            <div className="mt-4 inline-flex items-center text-orange-600 group-hover:text-orange-700 font-medium transition-colors">
              Accéder à la configuration
              <ArrowLeftIcon className="h-4 w-4 ml-2 rotate-180 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>

          <div className="p-6 bg-light-gray rounded-lg">
            <h4 className="font-medium text-dark-black mb-3">Configuration rapide</h4>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Pont Principal - 192.168.1.100:9090</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Pont Entrée - 192.168.1.101:8080</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span>Pont Sortie - 192.168.1.102:9091</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">💡 Conseils de configuration</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Utilisez des ports différents pour chaque pont bascule</li>
            <li>• Configurez des adresses IP statiques pour une stabilité réseau</li>
            <li>• Testez la connexion après chaque modification</li>
            <li>• Documentez la localisation physique de chaque pont</li>
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-medium text-dark-black mb-4">Statut des connexions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="group p-4 bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-medium text-green-800">Pont Principal</span>
              </div>
              <button className="opacity-0 group-hover:opacity-100 p-1 text-green-600 hover:text-green-800 hover:bg-green-200 rounded transition-all duration-200">
                <CogIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-green-700 mb-3">Connecté - 192.168.1.100:9090</p>
            <div className="flex space-x-2">
              <button className="flex-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors">
                Test
              </button>
              <button className="flex-1 px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded-lg transition-colors">
                Config
              </button>
            </div>
          </div>
          
          <div className="group p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                <span className="font-medium text-yellow-800">Pont Entrée</span>
              </div>
              <button className="opacity-0 group-hover:opacity-100 p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-200 rounded transition-all duration-200">
                <CogIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-yellow-700 mb-3">En attente - 192.168.1.101:8080</p>
            <div className="flex space-x-2">
              <button className="flex-1 px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded-lg transition-colors">
                Test
              </button>
              <button className="flex-1 px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs rounded-lg transition-colors">
                Config
              </button>
            </div>
          </div>
          
          <div className="group p-4 bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="font-medium text-red-800">Pont Sortie</span>
              </div>
              <button className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:text-red-800 hover:bg-red-200 rounded transition-all duration-200">
                <CogIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-red-700 mb-3">Déconnecté - 192.168.1.102:9091</p>
            <div className="flex space-x-2">
              <button className="flex-1 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg transition-colors">
                Test
              </button>
              <button className="flex-1 px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-lg transition-colors">
                Config
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'security':
        return renderSecuritySettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'quality-standards':
        return renderQualityStandardsSettings();
      case 'motifs-refoulement':
        return renderMotifsRefoulementSettings();
      case 'system':
        return renderSystemSettings();
      case 'ponts-bascule':
        return renderPontsBasculeSettings();
      default:
        return (
          <div className="bg-white rounded-xl shadow-sm p-6 text-center">
            <CogIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Module en cours de développement</p>
          </div>
        );
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <Link to="/dashboard" className="p-2 text-gray-400 hover:text-gray-600 mr-4">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-xl font-medium text-dark-black">Paramètres</h1>
          <p className="text-sm text-gray-600">Configuration du système et des préférences</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-bright-orange text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {renderTabContent()}
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-between items-center">
        {/* Message de statut */}
        {saveMessage && (
          <div className={`flex items-center px-4 py-2 rounded-lg ${
            saveMessage.type === 'success' 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {saveMessage.type === 'success' ? (
              <CheckIcon className="h-4 w-4 mr-2" />
            ) : (
              <XMarkIcon className="h-4 w-4 mr-2" />
            )}
            <span className="text-sm font-medium">{saveMessage.text}</span>
          </div>
        )}
        
        <button 
          onClick={saveAllSettings}
          disabled={isSaving}
          className="group flex items-center px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
              <span className="font-medium">Sauvegarde en cours...</span>
            </>
          ) : (
            <>
              <CheckIcon className="h-5 w-5 mr-3 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Sauvegarder les modifications</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
